import { useCallback, useEffect, useRef, useState } from 'react';
import type * as React from 'react';
import { useGarden } from '../../garden/GardenContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import './AppLayout.css';

const MIN_SIDE_PCT = 15;
const MAX_SIDE_PCT = 40;
const MIN_CENTER_PCT = 25;
const KEYBOARD_STEP_PCT = 2;

type Side = 'left' | 'right';

interface AppLayoutProps {
  left: React.ReactNode;
  center: React.ReactNode;
  right: React.ReactNode;
  isPaletteOpen: boolean;
  onClosePalette: () => void;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function AppLayout({ left, center, right, isPaletteOpen, onClosePalette }: AppLayoutProps) {
  const isMobile = useIsMobile();
  const { selectedElementIds, clearSelection } = useGarden();
  const containerRef = useRef<HTMLDivElement>(null);
  const [leftWidth, setLeftWidth] = useState(25);
  const [rightWidth, setRightWidth] = useState(25);
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [dragging, setDragging] = useState<Side | null>(null);
  const widthsRef = useRef({ leftWidth, rightWidth });
  widthsRef.current = { leftWidth, rightWidth };

  const startDrag = useCallback(
    (side: Side) => (event: React.PointerEvent) => {
      event.preventDefault();
      setDragging(side);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (side: Side) => (event: React.KeyboardEvent) => {
      let delta = 0;
      if (event.key === 'ArrowLeft')
        delta = side === 'left' ? -KEYBOARD_STEP_PCT : KEYBOARD_STEP_PCT;
      else if (event.key === 'ArrowRight')
        delta = side === 'left' ? KEYBOARD_STEP_PCT : -KEYBOARD_STEP_PCT;
      else return;

      event.preventDefault();
      const { leftWidth: currentLeft, rightWidth: currentRight } = widthsRef.current;
      if (side === 'left') {
        const max = Math.min(MAX_SIDE_PCT, 100 - currentRight - MIN_CENTER_PCT);
        setLeftWidth((w) => clamp(w + delta, MIN_SIDE_PCT, max));
      } else {
        const max = Math.min(MAX_SIDE_PCT, 100 - currentLeft - MIN_CENTER_PCT);
        setRightWidth((w) => clamp(w + delta, MIN_SIDE_PCT, max));
      }
    },
    [],
  );

  useEffect(() => {
    if (!dragging) return undefined;

    function handleMove(event: PointerEvent) {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const { leftWidth: currentLeft, rightWidth: currentRight } = widthsRef.current;

      if (dragging === 'left') {
        const pct = ((event.clientX - rect.left) / rect.width) * 100;
        const max = Math.min(MAX_SIDE_PCT, 100 - currentRight - MIN_CENTER_PCT);
        setLeftWidth(clamp(pct, MIN_SIDE_PCT, max));
      } else {
        const pct = ((rect.right - event.clientX) / rect.width) * 100;
        const max = Math.min(MAX_SIDE_PCT, 100 - currentLeft - MIN_CENTER_PCT);
        setRightWidth(clamp(pct, MIN_SIDE_PCT, max));
      }
    }

    function handleUp() {
      setDragging(null);
    }

    document.addEventListener('pointermove', handleMove);
    document.addEventListener('pointerup', handleUp);
    document.body.classList.add('app-layout--resizing');
    return () => {
      document.removeEventListener('pointermove', handleMove);
      document.removeEventListener('pointerup', handleUp);
      document.body.classList.remove('app-layout--resizing');
    };
  }, [dragging]);

  if (isMobile) {
    const hasSelection = selectedElementIds.length > 0;
    return (
      <div className="app-layout app-layout--mobile">
        <div className="app-layout__stage">{center}</div>

        {isPaletteOpen && (
          /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions --
             Backdrop click-to-dismiss; the "Close menu" button below is the keyboard-accessible path. */
          <div className="app-layout__drawer-overlay" onClick={onClosePalette}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions --
                stopPropagation only, not a user action. */}
            <div className="app-layout__drawer" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                className="app-layout__drawer-close"
                onClick={onClosePalette}
                aria-label="Close menu"
              >
                ×
              </button>
              {left}
            </div>
          </div>
        )}

        {hasSelection && (
          /* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions --
             Backdrop click-to-dismiss; DetailPanel/BulkActionsPanel render their own "Close" button. */
          <div className="app-layout__sheet-overlay" onClick={clearSelection}>
            {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions --
                stopPropagation only, not a user action. */}
            <div className="app-layout__sheet" onClick={(e) => e.stopPropagation()}>
              {right}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="app-layout" ref={containerRef}>
      <div
        className={`app-layout__pane app-layout__pane--left${leftOpen ? '' : ' app-layout__pane--collapsed'}`}
        style={leftOpen ? { width: `${leftWidth}%` } : undefined}
      >
        <button
          type="button"
          className="app-layout__toggle"
          onClick={() => setLeftOpen((open) => !open)}
          aria-label={leftOpen ? 'Collapse left panel' : 'Expand left panel'}
          aria-expanded={leftOpen}
          title={leftOpen ? 'Collapse panel' : 'Expand panel'}
        >
          <span aria-hidden="true">{leftOpen ? '‹' : '›'}</span>
        </button>
        {leftOpen && <div className="app-layout__pane-content">{left}</div>}
      </div>
      {/* jsx-a11y classifies role="separator" as non-interactive, but the WAI-ARIA APG's
          "Separator (Movable)" pattern explicitly requires tabindex + key handling on a
          focusable separator to make it keyboard-resizable, for both resizers below. */}
      {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      {leftOpen && (
        <div
          className={`app-layout__resizer${dragging === 'left' ? ' app-layout__resizer--active' : ''}`}
          onPointerDown={startDrag('left')}
          onKeyDown={handleKeyDown('left')}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize left panel"
          tabIndex={0}
        />
      )}
      <div className="app-layout__pane app-layout__pane--center">{center}</div>
      {rightOpen && (
        <div
          className={`app-layout__resizer${dragging === 'right' ? ' app-layout__resizer--active' : ''}`}
          onPointerDown={startDrag('right')}
          onKeyDown={handleKeyDown('right')}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize right panel"
          tabIndex={0}
        />
      )}
      {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
      <div
        className={`app-layout__pane app-layout__pane--right${rightOpen ? '' : ' app-layout__pane--collapsed'}`}
        style={rightOpen ? { width: `${rightWidth}%` } : undefined}
      >
        <button
          type="button"
          className="app-layout__toggle"
          onClick={() => setRightOpen((open) => !open)}
          aria-label={rightOpen ? 'Collapse right panel' : 'Expand right panel'}
          aria-expanded={rightOpen}
          title={rightOpen ? 'Collapse panel' : 'Expand panel'}
        >
          <span aria-hidden="true">{rightOpen ? '›' : '‹'}</span>
        </button>
        {rightOpen && <div className="app-layout__pane-content">{right}</div>}
      </div>
    </div>
  );
}

export default AppLayout;
